import type {ButtonGroupProps} from '@chakra-ui/react';
import {forwardRef, useRadioGroup} from '@chakra-ui/react';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import type {ComponentType, ForwardedRef, ReactElement} from 'react';
import {Children, cloneElement, isValidElement, useMemo} from 'react';

import type {RadioButtonProps} from './RadioButton';
import {ButtonGroupProvider, useStyles} from './context';

type Props<T extends string = string> = Omit<ButtonGroupProps, 'onChange' | 'variant' | 'isAttached'> & {
  name?: string;
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  ref?: ForwardedRef<any>;
};

const RadioButtonGroupInternal = <T extends string>(props: Props<T>, ref: ForwardedRef<any>) => {
  const {group} = useStyles();
  const {children, name, defaultValue, value, onChange, size, colorScheme, orientation, ...rest} = props;
  const {getRootProps, getRadioProps, isDisabled} = useRadioGroup({
    name,
    defaultValue,
    value,
    onChange,
    isDisabled: props.isDisabled,
    isNative: true,
  });

  const buttons = useMemo(
    () =>
      Children.toArray(children)
        .filter<ReactElement<RadioButtonProps>>(isValidElement)
        .map((button, index, array) => {
          const isFirstItem = index === 0;
          const isLastItem = array.length === index + 1;

          const positionProps = Object.assign({
            ...(isFirstItem && !isLastItem ? {[`data-first`]: ``} : {}),
            ...(!isFirstItem && isLastItem ? {[`data-last`]: ``} : {}),
            ...(!isFirstItem && !isLastItem ? {[`data-middle`]: ``} : {}),
          });

          return cloneElement(button, {
            ...positionProps,
            isDisabled: button.props.isDisabled || isDisabled,
            isChecked: button.props.value === value,
            radioProps: getRadioProps({
              value: button.props.value,
              disabled: isDisabled || button.props.isDisabled,
              isDisabled: isDisabled || button.props.isDisabled,
              isChecked: button.props.value === value,
              defaultChecked: button.props.value === defaultValue,
            }),
          });
        }),
    [children, isDisabled, value, getRadioProps, defaultValue],
  );

  return (
    <ButtonGroupProvider
      value={{
        isDisabled: props.isDisabled,
        colorScheme,
        orientation,
        size,
      }}
    >
      <ChakraDiv __css={group} data-orientation={orientation} role={`group`} {...getRootProps(rest)} ref={ref}>
        {buttons}
      </ChakraDiv>
    </ButtonGroupProvider>
  );
};

// @ts-expect-error - React 19 type compatibility issue with RadioButtonGroup component
export const RadioButtonGroup = forwardRef<Props, typeof RadioButtonGroupInternal>((props, ref) =>
  RadioButtonGroupInternal(props, ref),
) as ComponentType<Props>;
export type {Props as RadioButtonGroupProps};
